import { ChevronRight, Loader2, Plus, Tag, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { DataLabel } from '@/services/api/lake'
import { cn } from '@/utils/cn'
import { ColorDot } from './ColorDot'
import { LevelBadge } from './LevelBadge'

export interface LabelListProps {
  labels: DataLabel[]
  selectedLabelId: string | null
  loading: boolean
  onSelect: (id: string) => void
  onCreate: () => void
  onDelete: (label: DataLabel) => void
}

export function LabelList({
  labels,
  selectedLabelId,
  loading,
  onSelect,
  onCreate,
  onDelete,
}: LabelListProps) {
  const { t } = useTranslation()
  return (
    <Card className="max-md:gap-0! max-md:overflow-hidden max-md:pb-0">
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-2 text-sm max-md:row-span-2 max-md:self-center">
          <Tag className="h-4 w-4" />
          {t('admin.labels.labelsCount')}
          <span className="text-muted-foreground font-normal">({labels.length})</span>
        </CardTitle>
        <CardAction>
          <Button size="sm" className="h-9 text-xs md:h-7" onClick={onCreate}>
            <Plus className="h-3 w-3 mr-1" />
            {t('admin.labels.newLabel')}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : labels.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Tag className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">{t('admin.labels.noLabels')}</p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              {t('admin.labels.noLabelsHint')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border max-lg:max-h-[45dvh] max-lg:overflow-y-auto">
            {labels.map(label => (
              <div
                key={label.id}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors group',
                  selectedLabelId === label.id ? 'bg-accent' : 'hover:bg-accent/50'
                )}
                onClick={() => onSelect(label.id)}
              >
                <ColorDot color={label.color} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 max-md:flex-wrap max-md:gap-y-1">
                    <span className="text-sm font-medium text-foreground truncate">
                      {label.name}
                    </span>
                    <LevelBadge level={label.level} />
                  </div>
                  {label.description && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {label.description}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity max-md:opacity-100 md:h-7 md:w-7 [@media(hover:none)]:opacity-100"
                  onClick={e => {
                    e.stopPropagation()
                    onDelete(label)
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                <ChevronRight
                  className={cn(
                    'h-4 w-4 shrink-0 text-muted-foreground transition-colors',
                    selectedLabelId === label.id && 'text-selected-foreground'
                  )}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
